<template>
  <div>
    <div class="card card-white">
      <BaseDataTable
        :columns="columns"
        :entries="formattedContacts"
        :loading="loading"
        :default-page-length="pageLength"
        :default-page-length-options="pageLengthOptions"
        :search-placeholder="searchPlaceholder"
        max-height="78vh"
        tall-rows
        @row-click="$emit('row-click', $event)"
      >
        <template v-if="withStatus" #additional-control-bar-elements>
          <WdsSelect
            v-model="statusFilters"
            :list="statusFilterOptions"
            data-test="contacts-filter-status"
            :placeholder="allContactsName"
            multi-select
            can-deselect
            max-labels-visible="1"
            labels-primary
          >
            <template #select-all-text>
              <WdsText :text="$t('Select all')" />
            </template>
          </WdsSelect>
        </template>
        <template #no-results>
          <div class="tw-justify-center" data-test="no-contacts-found">
            {{ loading ? $t('Loading') : $t(noResultsMessage) }}
          </div>
        </template>
        <template #Name="{ row, rowIndex }">
          <slot name="Name" :row="row" :row-index="rowIndex">
            <div class="row-name">
              <WdsStack align-center gap-md>
                <ProfileInfoCard :global-id="row.id">
                  <WdsAvatar
                    :initials="row.initials"
                    :path="row.hasPicture ? row.picture : undefined"
                  />
                </ProfileInfoCard>
                <div>
                  <strong class="f-500">
                    <a v-if="row.profileUrl" :href="row.profileUrl">
                      {{ row.name }}
                    </a>
                    <span v-else>
                      {{ row.name }}
                    </span>
                  </strong>
                </div>
                <div class="name-tags">
                  <span v-for="tag in row.tags" :key="tag" class="label label-default m-l-10">
                    {{ tag }}
                  </span>
                </div>
              </WdsStack>
            </div>
          </slot>
        </template>
        <template #Fee="{ row, rowIndex }">
          <slot name="Fee" :row="row" :row-index="rowIndex"></slot>
        </template>
        <template #Ownership="{ row, rowIndex }">
          <slot name="Ownership" :row="row" :row-index="rowIndex"></slot>
        </template>
        <template #Status="{ row, rowIndex }">
          <slot name="Status" :row="row" :row-index="rowIndex"></slot>
        </template>
        <template #Type="{ row, rowIndex }">
          <slot name="Type" :row="row" :row-index="rowIndex"></slot>
        </template>
        <template #Tags="{ row, rowIndex }">
          <slot name="Tags" :row="row" :row-index="rowIndex"></slot>
        </template>
        <template #Action="{ row, rowIndex }">
          <WdsStack justify-center gap-sm>
            <slot
              name="editAction"
              :row-index="rowIndex"
              :global-id="row.id"
              :pivot-id="row.pivotId"
              :freelancer-name="row.name"
              :freelancer-email="row.email"
              :rates="row.rates"
              :currency="row.currency"
              :fee="row.fee"
              :ownership="row.ownership"
              :job-title="row.jobTitle"
              :tags="row.tags"
              :manages-workers="row.managesWorkers"
              :has-hires="row.hasHires"
            />
            <slot
              name="deleteAction"
              :contact-id="row.pivotId"
              :contact-name="row.name"
              :row-index="rowIndex"
            />
          </WdsStack>
        </template>
      </BaseDataTable>
    </div>
  </div>
</template>

<script>
import { WdsAvatar, WdsSelect, WdsStack, WdsText } from '@worksome/wds-components'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import ProfileInfoCard from '@/components/ProfileInfoCard.vue'

export default {
  name: 'ContactsTable',
  components: {
    WdsText,
    WdsSelect,
    ProfileInfoCard,
    BaseDataTable,
    WdsAvatar,
    WdsStack,
  },
  props: {
    /**
     * Contacts must be formatted such as
     * [{
     *   id // contact account local Id
     *   picture // contact account picture
     *   name // contact account name
     *   initials // contact account initials
     *   status // contact pivot status
     *   email // contact pivot inviteEmail
     *   pivotId // contact pivot localId
     * }]
     */
    contacts: {
      type: Array,
      required: true,
    },
    noResultsMessage: {
      type: String,
      default: "You don't have any contacts yet.",
    },
    allContactsName: {
      type: String,
      default: $t('All contacts'),
    },
    paginatorInfo: {
      type: Object,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    withStatus: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
    withEmail: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
    withoutFee: {
      type: Boolean,
      default: false,
    },
    withType: {
      type: Boolean,
      default: false,
    },
    withOwnership: {
      type: Boolean,
      default: false,
    },
    withTags: {
      type: Boolean,
      default: false,
    },
    pageLength: {
      type: Number,
      default: 50,
    },
    pageLengthOptions: {
      type: Array,
      default: [10, 50, 500],
    },
    searchPlaceholder: {
      type: String,
      default: $t('Search'),
    },
    lockedWidth: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
  },
  data() {
    const columns = [
      {
        slot: 'Name',
        key: 'name',
        title: $t('Name'),
        order: 'asc',
        orderAble: true,
        searchKeys: ['name', 'tags'],
      },
    ]

    if (this.withEmail) {
      columns.push({
        slot: 'Email',
        key: 'email',
        title: $t('Email'),
        class: 'vmiddle',
        orderAble: true,
        searchKeys: ['email'],
      })
    }

    if (this.withType) {
      columns.push({
        slot: 'Type',
        key: 'type',
        title: $t('Type'),
        class: 'vmiddle',
      })
    }

    if (!this.withoutFee) {
      columns.push({
        slot: 'Fee',
        key: 'fee',
        title: this.withType ? $t('Referral fee') : $t('Fee'),
        class: 'vmiddle',
        orderAble: true,
        searchKeys: ['fee'],
      })
    }

    if (this.withOwnership) {
      columns.push({
        slot: 'Ownership',
        key: 'ownership',
        title: $t('Ownership days'),
        class: 'vmiddle',
        orderAble: true,
        searchKeys: ['ownership'],
      })
    }

    if (this.withStatus) {
      columns.push({
        slot: 'Status',
        key: 'status',
        title: $t('Status'),
        class: 'vmiddle',
        orderAble: true,
      })
    }

    if (this.withTags) {
      columns.push({
        slot: 'Tags',
        key: 'tags',
        class: 'vmiddle',
        title: $t('Tags'),
        orderAble: false,
      })
    }

    columns.push({
      slot: 'Action',
      key: 'action',
      title: '',
      class: 'vmiddle',
      orderAble: false,
    })

    return {
      columns,
      statusFilterOptions: [
        { label: $t('invited'), value: 'INVITED' },
        { label: $t('active'), value: 'ACTIVE' },
      ],
      statusFilters: [],
    }
  },
  computed: {
    formattedContacts() {
      let formattedEntries = this.contacts

      if (this.statusFilters.length >= 1) {
        const filterNames = this.statusFilters.map((filter) => filter.label)
        formattedEntries = formattedEntries.filter((entry) => filterNames.includes(entry.status))
      }

      return formattedEntries
    },
  },
  methods: {
    search(search) {
      this.$emit('search', search)
    },
  },
}
</script>

<style lang="scss">
.name-tags {
  display: flex;
  flex-wrap: wrap;
}
</style>
